import {pushEvent} from "tracking/core/dispatcher";
import {EVENT_NAMES} from "tracking/core/eventNames";

const SCROLL_THRESHOLDS = [25, 50, 75, 90] as const;
type Threshold = (typeof SCROLL_THRESHOLDS)[number];

let isTrackingStarted = false;
let sentThresholds = new Set<Threshold>();
let isFrameScheduled = false;
let currentPageViewId = 0;

function getScrollPercent(): number {
    if (typeof window === "undefined" || typeof document === "undefined") return 0;

    const {documentElement, body} = document;
    const scrollTop =
        window.pageYOffset ||
        documentElement.scrollTop ||
        body.scrollTop ||
        0;

    const documentHeight = Math.max(
        body.scrollHeight,
        documentElement.scrollHeight,
        body.offsetHeight,
        documentElement.offsetHeight,
        body.clientHeight,
        documentElement.clientHeight
    );

    const viewportHeight = window.innerHeight || documentElement.clientHeight || 0;
    const scrollableHeight = Math.max(1, documentHeight - viewportHeight);

    return Math.min(100, Math.max(0, (scrollTop / scrollableHeight) * 100));
}

function getNewlyReachedThresholds(scrollPercent: number): Threshold[] {
    return SCROLL_THRESHOLDS.filter(
        (threshold) => scrollPercent >= threshold && !sentThresholds.has(threshold)
    );
}

async function trackScrollThreshold(percentage: Threshold) {
    const dedupeKey = `scroll:pageView:${currentPageViewId}:${percentage}`;
    await pushEvent(EVENT_NAMES.SCROLL, {percentage}, {dedupeKey});
}

function scheduleScrollEvaluation() {
    if (isFrameScheduled || typeof window === "undefined") return;

    isFrameScheduled = true;

    window.requestAnimationFrame(async () => {
        try {
            const scrollPercent = getScrollPercent();
            const newlyReachedThresholds = getNewlyReachedThresholds(scrollPercent);

            for (const threshold of newlyReachedThresholds) {
                await trackScrollThreshold(threshold);
                sentThresholds.add(threshold);
            }
        } finally {
            isFrameScheduled = false;
        }
    });
}

export function startScrollTracking() {
    if (isTrackingStarted || typeof window === "undefined") return;

    isTrackingStarted = true;
    currentPageViewId = 1;
    sentThresholds = new Set();
    isFrameScheduled = false;

    window.addEventListener("scroll", scheduleScrollEvaluation, {passive: true});
    window.addEventListener("resize", scheduleScrollEvaluation);

    scheduleScrollEvaluation();
}

export function stopScrollTracking() {
    if (!isTrackingStarted || typeof window === "undefined") return;

    window.removeEventListener("scroll", scheduleScrollEvaluation);
    window.removeEventListener("resize", scheduleScrollEvaluation);

    isTrackingStarted = false;
    sentThresholds = new Set();
    isFrameScheduled = false;
    currentPageViewId = 0;
}

export function resetScrollTrackingForPage() {
    if (typeof window === "undefined") return;

    if (!isTrackingStarted) {
        startScrollTracking();
        return;
    }

    currentPageViewId += 1;
    sentThresholds = new Set();
    isFrameScheduled = false;

    scheduleScrollEvaluation();
}